import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Subscription } from "rxjs";
import {
  ApiCallResponse,
  PageEventTypeEnum,
} from "../../../Model/ApiCallResponse";
import { ApiCallState } from "../../../Model/Enums/ApiCallState";
import { OrderStatusEnum } from "../../../Model/Enums/OrderStatusEnums";
import { OrderDisplayDto, OrderDto } from "../../../Model/OrderModel";
import { DateUtil } from "../../../Services/DateService/dateUtil";
import { OrderService } from "../../../Services/OrderService";
import {
  CartStore,
  CatalogueStore,
  ClientAllOrderStore,
  OrderStore,
  apiLoadingSubject,
} from "../../../Store";
import { messageConstants } from "../../../StringConstants";
import NewApprovePendingOrder from "./NewApprovePendingOrder";
import "./NewOrder.css";
import NewOrderHistory from "./NewOrderHistory";
import noDataFound from "../../../Components/Images/NoData.jpeg";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../utils/Toastify/ToastifyHandler";
import TableLoading from "../../LoadingComponent/TableLoading";
import { CartItem } from "../../../Model/CartModel";
import { PublishEventService } from "../../../Services/PublishEventService";
import { OrderItemsDto } from "../../../Model/OrderItems";
import { ProductModel } from "../../../Model/CatalogueModel";
import { NewCatalogueService } from "../../../Services/NewCatalogueService";

const NewOrder = () => {
  const navigate = useNavigate();
  const [clientAllOrders, setClientAllOrders] = useState<OrderDisplayDto[]>(
    new Array<OrderDisplayDto>()
  );
  const [isClientOrderLoading, setIsClientAllOrderLoading] =
    React.useState<boolean>(true);
  const [isPendingOrder, setIsPendingOrder] = useState<number | null>(0);

  const orderService = new OrderService();
  const date = new DateUtil();

  const getClientAllOrders = () => {
    orderService.getClientAllOrders();
  };

  const toggleStatus = (index: number) => {
    setIsPendingOrder((prevState) => (prevState === index ? index : index));
  };
  const mergeOrderWithCatalogue = (
    orderItems: OrderItemsDto[],
    catalogue: ProductModel[]
  ) => {
    const newCart = new Map<string, CartItem>();
    const missingProducts: string[] = [];

    orderItems.forEach((orderItem) => {
      const pid = orderItem.product.pid;
      const latest = catalogue.find((c) => c.productId === pid);

      if (!latest) {
        missingProducts.push(orderItem.product.pname);
        return;
      }

      const cost = Number(latest.cost || 0);
      const discount = Number(latest.discount || 0);
      const gst = Number(latest.gst || 0);

      const discountedPrice = Number((cost * (1 - discount / 100)).toFixed(2));

      newCart.set(pid.toString(), {
        gst,
        discountedPrice,
        applicableDiscount: discount,
        qty: orderItem.qty,
        avlQty: latest.productQty?.qty ?? 0,
        product: {
          pid: latest.productId,
          pname: latest.productName,
          photo: latest.photo,
          cost,
        },
      });
    });
    if (newCart.size === 0) {
      showErrorToast(
        `${missingProducts.join(
          ", "
        )} is no longer available. Reorder not possible.`
      );
      return;
    }

    CartStore.setCart(newCart);

    const publishEvent = new PublishEventService();
    publishEvent.changed(PageEventTypeEnum.CART);

    showSuccessToast(messageConstants.ITEMS_ADDED_TO_CART);

    if (missingProducts.length > 0) {
      showErrorToast(
        `${
          missingProducts.length
        } items could not be added because they are no longer available: ${missingProducts.join(
          ", "
        )}`
      );
    }

    navigate("/newCart");
  };

  useEffect(() => {
    let orderSubscriber: Subscription;
    let reorderOrder: OrderDto | null = null;
    let catalogueProducts: ProductModel[] = [];
    getClientAllOrders();
    orderSubscriber = apiLoadingSubject.subscribe(
      (response: ApiCallResponse) => {
        if (PageEventTypeEnum.ALL_ORDER === response.eventType) {
          switch (response.apiCallState) {
            case ApiCallState.LOADING:
              setIsClientAllOrderLoading(true);
              break;
            case ApiCallState.LOADING_SUCCESS:
              setClientAllOrders(ClientAllOrderStore.getOrder());
              setIsClientAllOrderLoading(false);
              break;
            case ApiCallState.LOADING_ERROR:
              showErrorToast(response.data ?? messageConstants.REFRESH_PAGE);
              break;

            case ApiCallState.RELOAD:
              getClientAllOrders();
          }
        }
        if (response.eventType === PageEventTypeEnum.ORDER_BY_ID) {
          switch (response.apiCallState) {
            case ApiCallState.LOADING_SUCCESS:
              reorderOrder = OrderStore.getOrderById();
              if (reorderOrder && catalogueProducts.length > 0) {
                mergeOrderWithCatalogue(
                  reorderOrder.orderItems,
                  catalogueProducts
                );
              }
              break;
          }
        }
        if (response.eventType === PageEventTypeEnum.CATALOGUE) {
          switch (response.apiCallState) {
            case ApiCallState.LOADING_SUCCESS:
              let categories = CatalogueStore.getCatalogue();
              catalogueProducts = categories?.catalogueResponse || [];

              if (reorderOrder && catalogueProducts.length > 0) {
                mergeOrderWithCatalogue(
                  reorderOrder.orderItems,
                  catalogueProducts
                );
              }
              break;
          }
        }
      }
    );

    return () => {
      if (orderSubscriber) {
        orderSubscriber.unsubscribe();
      }
    };
  }, []);
  const navigateToOrderDetail = (orderId: number) => {
    if (!isClientOrderLoading) {
      navigate(`/OrderDetails/${orderId}`);
    }
  };
  const orderStatus = ["Open Orders", "Approval Pending", "Order History"];
  const handleReorder = (orderId: number) => {
    orderService.getOrderByOrderId(orderId);
    const newCatalogueDataService = new NewCatalogueService();
    newCatalogueDataService.getCatalogue();
  };

  return (
    <>
      <div className="heading-wrap mt-3">
        <p className="fw-bold mb-2 mb-sm-0 me-3 me-sm-0">Orders</p>

        <div className="d-flex flex-wrap flex-sm-nowrap align-items-center justify-content-between">
          <div className="page_path border-bottom">
            <ul
              className="nav nav-pills mb-2 mb-md-3"
              id="pills-tab"
              role="tablist"
            >
              {orderStatus.map((item, index) => {
                return (
                  <li className="nav-item px-2" key={index}>
                    <span
                      role="button"
                      className={
                        isPendingOrder === index
                          ? "text-success text-decoration-underline"
                          : ""
                      }
                      onClick={() => toggleStatus(index)}
                    >
                      {item}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      {isPendingOrder === 0 && (
        <>
          <div className="table-responsive mt-4 col-7 table-fixed table-wrap">
            {isClientOrderLoading ? (
              <TableLoading />
            ) : clientAllOrders.length > 0 ? (
              <table className="default_table table table-hover ">
                <thead className="Ordertable_head">
                  <tr className="green_clr">
                    <th scope="col"> Order ID</th>
                    <th scope="col"> Ordered Date</th>
                    <th scope="col">Expected Delivery Date</th>
                    <th scope="col">Email</th>
                    <th scope="col">Status</th>
                    <th scope="col">Billing Amount</th>
                    <th scope="col">Payment Status</th>
                    <th scope="col">Order Details</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clientAllOrders.map((item, index) => {
                    if (
                      item.orderStatus === OrderStatusEnum.APPROVAL_PENDING ||
                      item.orderStatus === OrderStatusEnum.COMPLETED
                    ) {
                      return false;
                    }
                    return (
                      <tr key={index}>
                        <td className="fs-14">{item.orderIdentifier}</td>
                        <td className="fs-14">
                          {date.getDateFromEpoch(item.createdOn)}
                        </td>
                        <td className="fs-14">
                          {date.calculateExpectedDeliveryDate(item.createdOn)}
                        </td>
                        <td className="fs-14 fw-500">{item.createdByEmail}</td>
                        <td className="fs-14 fw-500">
                          {item.orderStatus === "PENDING"
                            ? "Placed"
                            : item.orderStatus}
                        </td>
                        <td className="fs-14 ">
                          {`₹ ${(+item.billingAmount.toFixed(2)).toLocaleString(
                            "en-IN"
                          )}`}
                        </td>
                        <td className="fs-14">{item.paymentStatus}</td>
                        {item.orderStatus === OrderStatusEnum.CANCELLED ? (
                          <td>NA</td>
                        ) : (
                          <td className="table-hover">
                            <span
                              role="button"
                              onClick={() => navigateToOrderDetail(item.id)}
                              className="text-primary"
                            >
                              View Order
                            </span>
                          </td>
                        )}
                        {item.orderStatus === OrderStatusEnum.DELIVERED ? (
                          <td className="d-flex flex-column">
                            <button
                              className="btn btn-success p-1 m-1 rounded"
                              onClick={() => handleReorder(item.id)}
                            >
                              Reorder
                            </button>
                          </td>
                        ) : (
                          <td>-</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="image-pos">
                <img className="no-data-found" src={noDataFound} alt="" />
              </div>
            )}
          </div>
        </>
      )}
      {isPendingOrder === 1 ? (
        <>
          <NewApprovePendingOrder />
        </>
      ) : (
        <> {isPendingOrder === 2 && <NewOrderHistory />} </>
      )}
    </>
  );
};

export default NewOrder;
